import type { Session } from '@supabase/supabase-js';
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';

interface AdminProfile {
  id: string;
  email: string | null;
  full_name: string | null;
  is_admin: boolean;
}

interface AuthContextValue {
  session: Session | null;
  profile: AdminProfile | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    // email / is_admin are owner-only columns: read the own row via
    // get_my_profile (resolves auth.uid()) and keep the same projection.
    const { data, error } = await supabase.rpc('get_my_profile').maybeSingle();

    if (error) {
      console.warn('[marketing-auth] profile fetch failed:', error.message);
      setProfile(null);
      return;
    }
    const row = data as AdminProfile | null;
    setProfile(
      row ? { id: row.id, email: row.email, full_name: row.full_name, is_admin: row.is_admin } : null,
    );
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Resolve `loading` even when loadProfile rejects — see editor's
    // useAuth for the original incident this guards against.
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        setSession(data.session);
        if (data.session?.user) {
          try {
            await loadProfile();
          } catch (e) {
            console.warn('[marketing-auth] initial loadProfile failed:', e);
          }
        }
      } catch (e) {
        console.warn('[marketing-auth] getSession failed:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // Same deadlock guard as the editor: never await Supabase inside
    // onAuthStateChange — the SDK still holds its auth lock and any
    // db call re-enters it and hangs forever.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (nextSession?.user) {
        setTimeout(() => {
          loadProfile().catch((e) => {
            console.warn('[marketing-auth] loadProfile on auth change failed:', e);
          });
        }, 0);
      } else {
        setProfile(null);
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signOut = useCallback(async () => {
    // This browser only: the app and other devices stay signed in (#1695).
    await supabase.auth.signOut({ scope: 'local' });
    setSession(null);
    setProfile(null);
  }, []);

  const value: AuthContextValue = { session, profile, loading, signOut };
  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used inside <AuthProvider>');
  return ctx;
}
